import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'

import { Skeleton } from '@/components/Skeleton'
import { DialektWahl } from '@/features/translate/DialektWahl'
import { Eingabe } from '@/features/translate/Eingabe'
import { ErgebnisKarte } from '@/features/translate/ErgebnisKarte'
import { useSprachausgabe, type Verfuegbarkeit } from '@/features/translate/useSprachausgabe'
import { useUebersetzung } from '@/features/translate/useUebersetzung'
import { fetchDialekte, fetchKontingent, type Mikrocopy } from '@/lib/api'

const GRUNDCOPY: Mikrocopy = {
  cta: 'Übertragen',
  leer: 'Schreib etwas.',
  laedt: 'Läuft…',
}

const STIMMHINWEIS: Record<Verfuegbarkeit, string> = {
  wird_geprueft: 'Stimmen werden geladen…',
  bereit: '',
  keine_stimme: 'Keine deutsche Stimme installiert — Anhören ist deshalb aus.',
  nicht_unterstuetzt: 'Dieser Browser kann nicht vorlesen.',
}

function kontingentText(rest: number, grenze: number): string {
  if (rest <= 0) return `Kontingent aufgebraucht (${grenze} in 24 Stunden)`
  if (rest === 1) return `Noch eine Übertragung von ${grenze} in 24 Stunden`
  return `Noch ${rest} von ${grenze} Übertragungen in 24 Stunden`
}

export function TranslateView() {
  const [text, setText] = useState('')
  const [dialektId, setDialektId] = useState<string | null>(null)
  const [spricht, setSpricht] = useState(false)
  const uebersetzung = useUebersetzung()
  const stimme = useSprachausgabe()

  const dialekte = useQuery({
    queryKey: ['dialekte'],
    queryFn: ({ signal }) => fetchDialekte(signal),
    staleTime: 5 * 60_000,
  })

  const kontingent = useQuery({
    queryKey: ['kontingent'],
    queryFn: ({ signal }) => fetchKontingent(signal),
    staleTime: 60_000,
  })

  const liste = useMemo(() => dialekte.data ?? [], [dialekte.data])

  // Abgeleitet statt in einem Effekt nachgezogen: solange nichts gewählt ist,
  // steht der erste Dialekt da — sonst wäre das Feld leer und der Knopf
  // grundlos gesperrt.
  const gewaehlt = liste.find((dialekt) => dialekt.id === dialektId) ?? liste[0] ?? null

  // Der Strom liefert den Reststand mit; die Abfrage ist nur der Startwert.
  const rest = uebersetzung.rest ?? kontingent.data?.rest ?? null
  const grenze = kontingent.data?.grenze ?? 0
  const erschoepft = grenze > 0 && rest !== null && rest <= 0

  const absenden = useCallback(() => {
    if (!gewaehlt) return
    stimme.stopp()
    setSpricht(false)
    uebersetzung.starte(text, gewaehlt.id)
  }, [gewaehlt, stimme, text, uebersetzung])

  const anhoeren = useCallback(
    (vorlesetext: string) => {
      if (spricht && stimme.spricht) {
        stimme.stopp()
        setSpricht(false)
        return
      }
      stimme.sprich(vorlesetext)
      setSpricht(true)
    },
    [spricht, stimme],
  )

  const hinweis = STIMMHINWEIS[stimme.verfuegbarkeit]

  return (
    <div className="flex w-full flex-col gap-8">
      <header className="animate-rise">
        <h1 className="font-display text-4xl tracking-tight sm:text-5xl">mundart</h1>
        <div className="motiv mt-3 w-24 rounded-full" aria-hidden="true" />
        <p className="text-on-surface-muted mt-3">
          Ein Text, ein Dialekt — {liste.length > 0 ? `${liste.length} stehen zur Wahl` : 'gleich'}.
        </p>
      </header>

      {dialekte.isPending && (
        <div className="bg-surface-container rounded-card border-outline border p-5">
          <Skeleton breiten={['30%', '60%']} />
        </div>
      )}

      {dialekte.isError && (
        <p role="alert" className="bg-surface-container rounded-card border-outline border p-5">
          Die Dialekte lassen sich nicht laden. Läuft das Backend?
        </p>
      )}

      {liste.length > 0 && (
        <DialektWahl
          dialekte={liste}
          gewaehlt={gewaehlt}
          onWaehlen={(dialekt) => {
            setDialektId(dialekt.id)
          }}
        />
      )}

      <Eingabe
        wert={text}
        onAendern={setText}
        onAbsenden={absenden}
        mikrocopy={gewaehlt?.mikrocopy ?? GRUNDCOPY}
        laeuft={uebersetzung.laeuft}
        bereit={gewaehlt !== null && !erschoepft}
      />

      {grenze > 0 && rest !== null && (
        <p
          aria-live="polite"
          className={`-mt-6 text-xs ${erschoepft ? 'text-on-surface' : 'text-on-surface-muted'}`}
        >
          {kontingentText(rest, grenze)}
          {erschoepft && ' — schon Übertragenes bleibt abrufbar.'}
        </p>
      )}

      {uebersetzung.lauf && gewaehlt && (
        <ErgebnisKarte
          dialekt={
            liste.find((dialekt) => dialekt.id === uebersetzung.lauf?.dialekt) ?? gewaehlt
          }
          lauf={uebersetzung.lauf}
          verfuegbarkeit={stimme.verfuegbarkeit}
          spricht={spricht && stimme.spricht}
          onAnhoeren={() => {
            anhoeren(uebersetzung.lauf?.ergebnis?.tts_text ?? uebersetzung.lauf?.text ?? '')
          }}
        />
      )}

      {uebersetzung.lauf?.ergebnis && hinweis && (
        <p className="text-on-surface-muted -mt-4 text-xs">{hinweis}</p>
      )}
    </div>
  )
}
